import { z } from "zod";

/** Imports from JobNimbus (spec §18): pull, review, commit. */

export type ImportKind = "contact" | "job" | "note" | "task";
export type ImportDecision = "import" | "skip" | "merge";
export type ImportRunStatus = "pulling" | "ready" | "committing" | "done" | "failed";

export const IMPORT_FLAG_LABELS: Record<string, string> = {
  junk: "Looks like junk",
  duplicate: "Duplicate",
  possible_duplicate: "Possible duplicate",
  matches_existing: "Already in the CRM",
  already_imported: "Imported before",
  no_customer: "No customer",
  not_a_customer: "Contact isn't a customer in JobNimbus",
  no_parent: "Its job or contact isn't coming",
  renumbered: "Job number taken",
  customer_skipped: "Customer skipped",
  parent_skipped: "Its job or contact is skipped",
};

export interface ImportRunSummary {
  id: string;
  source: "jobnimbus";
  status: ImportRunStatus;
  error: string | null;
  startedAt: string;
  startedBy: string | null;
  pulledAt: string | null;
  committedAt: string | null;
  /** Per kind: total, and counts by decision and by flag. */
  counts: Record<ImportKind, { total: number; decisions: Record<ImportDecision, number>; flags: Record<string, number> }>;
  options: ImportOptions;
  /** JobNimbus job types found, with how many jobs each. */
  jobTypes: { name: string; count: number }[];
  /** JobNimbus contact types found, and the CRM type each goes to. */
  contactTypes: { name: string; count: number; typeName: string; category: string }[];
  results: Record<string, Record<string, number>> | null;
}

export interface ImportOptions {
  /** JobNimbus job type → CRM workflow id. */
  workflowByType?: Record<string, string>;
  /** Jobs with JobNimbus's "Insurance Claim" box checked go to this workflow (unset: use the type's). */
  insuranceWorkflowId?: string | null;
}

export const importOptionsSchema = z.object({
  workflowByType: z.record(z.string().max(200), z.string().uuid()).optional(),
  insuranceWorkflowId: z.string().uuid().nullable().optional(),
});

export interface ImportRecordItem {
  id: string;
  kind: ImportKind;
  externalId: string;
  label: string;
  /** One-line summary: phone, email, address; job type and stage... */
  detail: string;
  flags: string[];
  reasons: string[];
  decision: ImportDecision;
  mergeInto: { kind: "contact" | "pulled"; id: string; label: string } | null;
  targetId: string | null;
  error: string | null;
  /** Contacts: the CRM contact type it comes in as, and its type in JobNimbus. */
  importsAs: { typeName: string; category: string; jnType: string } | null;
}

export const importRecordQuerySchema = z.object({
  kind: z.enum(["contact", "job", "note", "task"]).default("contact"),
  flag: z.string().max(40).optional(),
  decision: z.enum(["import", "skip", "merge"]).optional(),
  q: z.string().trim().max(100).optional(),
  /** Contacts with these JobNimbus types (comma-separated; one CRM type can come from several). */
  jnTypes: z.string().max(2000).optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Decide for a list of records, or for every record of a kind (optionally with a flag). */
export const importDecideSchema = z
  .object({
    ids: z.array(z.string().uuid()).max(5000).optional(),
    kind: z.enum(["contact", "job", "note", "task"]).optional(),
    flag: z.string().max(40).optional(),
    /** With kind contact: only contacts with these JobNimbus types. */
    jnTypes: z.array(z.string().max(120)).max(100).optional(),
    decision: z.enum(["import", "skip", "merge"]),
    /** For a single merge: the CRM contact to merge into. */
    mergeContactId: z.string().uuid().nullable().optional(),
  })
  .refine((d) => d.ids?.length || d.kind, { message: "Pick records" });
